import { Fragment, memo, useCallback, useContext, useState, useMemo } from "react"
import PropTypes from "prop-types"
import { Box, Tooltip, IconButton } from "@mui/material"
import { ArrowUpward, ArrowDownward } from "@mui/icons-material"
import classNames from "classnames"
import { sortDirLabel } from "helpers/sorts"
import CellTooltip from "components/securities-table/cell-tooltip"
import { getNextSortDirection } from "../helpers"
import { hasRenderableContent } from "../helpers/has-renderable-content"
import { resolveTooltipContents } from "../helpers/header-info-icon"
import { TOOLTIP_ENTER_DELAY_MS } from "helpers/constants"
import { getHeaderLabelWidth, getHeaderLayout, getHeaderName, isGoToFinderColumn, splitHeaderUnit } from "../helpers/header-layout"
import GoToFinder, { NO_MATCH_MESSAGE } from "./go-to-finder"
import { HeaderMaxLinesContext } from "./header-max-lines-context"
import { useRelayoutOnFontLoad } from "../hooks/use-relayout-on-font-load"

// createLabelColumn sets colDef.headerName to the idLabel each view passes in ("Factor" for
// every factors-view template, "Ticker"/unset elsewhere) — reusing it here avoids threading a
// separate label down through create-label-column.jsx and every asset/factors template.
const FACTORS_NO_MATCH_MESSAGE = "No matching factors"

const DELAY_TAG_SX = { typography: "caption", color: "text.secondary", lineHeight: 1 }

const SortButton = memo(({
  direction,
  isPreview = false,
  field,
  headerName,
  isAbsolute,
  onSortChange,
  getNextSortDirection: getNextSortDirectionProp,
}) => {
  const handleClick = useCallback((e) => {
    e.stopPropagation() // Prevent header click when clicking the button
    if (onSortChange && getNextSortDirectionProp) {
      const nextDirection = getNextSortDirectionProp()
      onSortChange(field, nextDirection)
    }
  }, [onSortChange, field, getNextSortDirectionProp])

  if (!direction) return null

  const ArrowIcon = direction === "asc" ? ArrowUpward : ArrowDownward

  return (
    <IconButton
      size="small"
      onClick={handleClick}
      className={classNames("MuiCustomHeader-sortIcon", {
        "MuiCustomHeader-sortIconAbsolute": isAbsolute && !isPreview,
        "MuiCustomHeader-sortIconPreview": isPreview,
      })}
      aria-label={`Sort ${headerName} ${direction === "asc" ? "ascending" : "descending"}`}
    >
      <ArrowIcon fontSize="inherit" />
    </IconButton>
  )
})

SortButton.displayName = "SortButton"

SortButton.propTypes = {
  direction: PropTypes.string,
  isPreview: PropTypes.bool,
  field: PropTypes.string.isRequired,
  headerName: PropTypes.string.isRequired,
  isAbsolute: PropTypes.bool,
  onSortChange: PropTypes.func,
  getNextSortDirection: PropTypes.func,
}

/**
 * HeaderTextButton - A button component that looks like plain text but supports
 * click interactions without interfering with MUI DataGrid column dragging.
 *
 * This component solves the issue where text elements with click handlers
 * prevent column drag-and-drop functionality in MUI DataGrid.
 *
 */
const HeaderTextButton = memo(({ children, onClick, disabled = false, className = "" }) => {
  return (
    <button
      className={classNames("MuiCustomHeader-text", className)}
      onClick={onClick}
      disabled={disabled}
      type="button"
    >
      {children}
    </button>
  )
})

HeaderTextButton.displayName = "HeaderTextButton"

HeaderTextButton.propTypes = {
  children: PropTypes.node.isRequired,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  className: PropTypes.string,
}

const HeaderLabel = memo(({ headerName, wordsClampLines, isUnitOnOwnLine }) => {
  const { words, unit } = splitHeaderUnit(headerName)

  return (
    <span className="MuiCustomHeader-text-wrapper">
      <span className="MuiCustomHeader-label" data-test-class="table-header-label-text">
        <span className="MuiCustomHeader-words" data-clamp-lines={wordsClampLines ?? undefined}>{words}</span>
        {unit && (
          <>
            {" "}
            <span className="MuiCustomHeader-unit" data-own-line={isUnitOnOwnLine ? "" : undefined}>{unit}</span>
          </>
        )}
      </span>
    </span>
  )
})

HeaderLabel.displayName = "HeaderLabel"

HeaderLabel.propTypes = {
  headerName: PropTypes.string.isRequired,
  wordsClampLines: PropTypes.number,
  isUnitOnOwnLine: PropTypes.bool.isRequired,
}

const TableHeader = memo(({
  field,
  colDef,
  sortMetricId,
  isAbsoluteSort,
  sortArrowDirection,
  metricMetadata,
  onSortChange,
  data,
  goToRows,
  onGoToRow,
}) => {
  const maxLines = useContext(HeaderMaxLinesContext)
  useRelayoutOnFontLoad()
  const [isHovered, setIsHovered] = useState(false)
  const [headerCellNode, setHeaderCellNode] = useState(null)

  const showGoToFinder = Boolean(goToRows && onGoToRow)

  // renderHeader only reaches the title-container content, not MuiDataGrid-columnHeader itself.
  const rootRef = useCallback((node) => {
    if (node) setHeaderCellNode(node.closest(".MuiDataGrid-columnHeader"))
  }, [])

  const isSortedColumn = sortMetricId === field
  const isAbsolute = isSortedColumn && isAbsoluteSort
  const metric = metricMetadata?.find(m => m.id === field)
  const isTextColumn = colDef.type === "string"

  const headerName = getHeaderName({ headerName: colDef.headerName, field })
  const hasGoToFinderLayout = isGoToFinderColumn(colDef)
  const headerLayout = getHeaderLayout(headerName, getHeaderLabelWidth(colDef.computedWidth, hasGoToFinderLayout), maxLines, !hasGoToFinderLayout)

  const tooltipContentsValue = useMemo(() => resolveTooltipContents(metric, data), [metric, data])

  const getNextSortDirectionForColumn = useCallback(() => {
    return getNextSortDirection({
      isSortedColumn,
      isTextColumn,
      isAbsolute,
      sortArrowDirection,
    })
  }, [isSortedColumn, isTextColumn, isAbsolute, sortArrowDirection])

  const handleHeaderClick = useCallback(() => {
    if (!onSortChange) return

    const nextDirection = getNextSortDirectionForColumn()
    onSortChange(field, nextDirection)
  }, [onSortChange, field, getNextSortDirectionForColumn])

  const getPreviewArrowDirection = useCallback(() => {
    if (isSortedColumn) return null
    const nextDirection = getNextSortDirectionForColumn()

    // If next direction is null (no sort), don't show preview arrow
    if (nextDirection === null) return null

    return nextDirection.includes("asc") ? "asc" : "desc"
  }, [isSortedColumn, getNextSortDirectionForColumn])

  const metricTooltipText = metric?.getTooltipText?.()

  const sortDescription = useMemo(() => {
    if (!isSortedColumn) return null
    const alphabetical = colDef.type === "string"
    const dir = sortArrowDirection
    const abs = isAbsolute
    return sortDirLabel({ dir, abs, alphabetical })
  }, [isSortedColumn, isAbsolute, sortArrowDirection, colDef.type])

  const hasMetricTooltipText = hasRenderableContent(metricTooltipText)
  const hasTooltipContent = Boolean(headerName || hasMetricTooltipText || sortDescription)
  const tooltipTitle = hasTooltipContent ? (
    <>
      {headerName && <div>{headerName}</div>}
      {hasMetricTooltipText && metricTooltipText}
      {sortDescription && <div>{sortDescription}</div>}
    </>
  ) : ""

  const HeaderContent = (
    <Box
      ref={rootRef}
      data-field={field}
      className={classNames("MuiCustomHeader-root", {
        "MuiCustomHeader-alignLeft": isTextColumn,
        "MuiCustomHeader-alignRight": !isTextColumn,
        "MuiCustomHeader-active": isSortedColumn,
        "MuiCustomHeader-clickable": onSortChange,
      })}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Box className="MuiCustomHeader-content">
        <HeaderTextButton
          onClick={handleHeaderClick}
          disabled={!onSortChange}
        >
          <HeaderLabel headerName={headerName} wordsClampLines={headerLayout.wordsClampLines} isUnitOnOwnLine={headerLayout.isUnitOnOwnLine} />
        </HeaderTextButton>

        <Box className="MuiCustomHeader-gutter">
          {isSortedColumn && (
            <SortButton
              direction={sortArrowDirection}
              field={field}
              headerName={headerName}
              isAbsolute={isAbsolute}
              onSortChange={onSortChange}
              getNextSortDirection={getNextSortDirectionForColumn}
            />
          )}

          {!isSortedColumn && isHovered && onSortChange && (
            <SortButton
              direction={getPreviewArrowDirection()}
              isPreview={true}
              field={field}
              headerName={headerName}
              isAbsolute={isAbsolute}
              onSortChange={onSortChange}
              getNextSortDirection={getNextSortDirectionForColumn}
            />
          )}

          {metric?.delayTag && (
            <Box component="span" sx={DELAY_TAG_SX}>{metric.delayTag}</Box>
          )}
          {tooltipContentsValue && (
            <CellTooltip contents={tooltipContentsValue} />
          )}
        </Box>
      </Box>
    </Box>
  )

  return (
    <Fragment>
      <Tooltip title={tooltipTitle} placement="top" enterDelay={TOOLTIP_ENTER_DELAY_MS}>
        {HeaderContent}
      </Tooltip>
      {showGoToFinder && (
        <GoToFinder
          rows={goToRows}
          onGoToRow={onGoToRow}
          headerCellNode={headerCellNode}
          noMatchMessage={headerName === "Factor" ? FACTORS_NO_MATCH_MESSAGE : NO_MATCH_MESSAGE}
        />
      )}
    </Fragment>
  )
})

TableHeader.displayName = "TableHeader"

TableHeader.propTypes = {
  field: PropTypes.string.isRequired,
  colDef: PropTypes.shape({
    headerName: PropTypes.string,
    type: PropTypes.string,
    computedWidth: PropTypes.number.isRequired,
  }).isRequired,
  sortMetricId: PropTypes.string,
  isAbsoluteSort: PropTypes.bool,
  sortArrowDirection: PropTypes.string,
  metricMetadata: PropTypes.array,
  onSortChange: PropTypes.func,
  data: PropTypes.object,
  goToRows: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string,
    children: PropTypes.object,
  })),
  onGoToRow: PropTypes.func,
}

export default TableHeader
